/**
 * Onboarding Checklist
 * Shows setup progress for new tenants on the dashboard.
 * Tracks completion via localStorage + actual metrics data.
 */

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Stack,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Close as CloseIcon,
  LocationOn as CampusIcon,
  Class as ClassIcon,
  People as PeopleIcon,
  Receipt as BillingIcon,
  EventNote as AttendanceIcon,
  RocketLaunch as RocketIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

interface ChecklistItem {
  key: string;
  label: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  /** Path to navigate when clicked */
  path: string;
  /** Help guide slug */
  helpSlug: string;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    key: "campus",
    label: "Create your first campus",
    description: "Set up your school's location with address and contact info.",
    icon: <CampusIcon />,
    color: "#f59e0b",
    path: "/campuses/new",
    helpSlug: "create-campus",
  },
  {
    key: "class",
    label: "Set up a class",
    description: "Create classes with schedules and capacity limits.",
    icon: <ClassIcon />,
    color: "#10b981",
    path: "/classes/new",
    helpSlug: "create-class",
  },
  {
    key: "family",
    label: "Add a family & student",
    description: "Register families and enroll their children.",
    icon: <PeopleIcon />,
    color: "#3b82f6",
    path: "/families/new",
    helpSlug: "add-family-students",
  },
  {
    key: "fees",
    label: "Configure fee types",
    description: "Set up tuition and other fees for billing.",
    icon: <BillingIcon />,
    color: "#ef4444",
    path: "/fees/types/new",
    helpSlug: "setup-fee-types",
  },
  {
    key: "attendance",
    label: "Take your first attendance",
    description: "Record student attendance for a class session.",
    icon: <AttendanceIcon />,
    color: "#06b6d4",
    path: "/attendance",
    helpSlug: "take-attendance",
  },
];

// Progress belongs to a school, not to a browser. These used to be global
// keys, so a super user (or anyone signing into a second school on the same
// machine) carried one school's setup progress into another and saw a
// checklist that misrepresented what was actually configured (audit Q04).
//
// Deliberately not migrated from the old global keys: inheriting them is the
// bug. A school that had dismissed the checklist sees it once more, already
// showing the steps its data has completed.
const dismissedKey = (tenantId: string) => `ilmiq_onboarding_dismissed:${tenantId}`;

/** localStorage can throw (private mode, disabled site data). */
const readDismissed = (tenantId: string): boolean => {
  try {
    return localStorage.getItem(dismissedKey(tenantId)) === "true";
  } catch {
    return false;
  }
};
const completedKey = (tenantId: string) => `ilmiq_onboarding_completed:${tenantId}`;

/** localStorage can throw (private mode, disabled site data). */
function readJson(key: string): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Progress is a convenience; losing it must never break the dashboard.
  }
}

interface OnboardingChecklistProps {
  /** Metrics to auto-detect completion */
  metrics?: {
    total_students?: number;
    total_families?: number;
    active_classes?: number;
    /** Classes created, whether or not anyone is enrolled yet. */
    setup_classes_count?: number;
    /** Families with at least one student, enrolled or not. */
    setup_families_with_students_count?: number;
    /** Attendance records saved today. A one-way signal: records today prove
     *  the school has taken attendance; none today proves nothing (weekend,
     *  holiday), which is why completion is persisted once earned. */
    attendance_total_today?: number;
  };
  /** Number of campuses */
  campusCount?: number;
  /** Number of fee types */
  feeTypeCount?: number;
}

export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  metrics,
  campusCount,
  feeTypeCount,
}) => {
  const navigate = useNavigate();
  const tenantId = useAuthStore((state) => state.user?.tenant_id) ?? "unknown";

  // Read synchronously for the first render: starting from `false` and
  // correcting in an effect painted the checklist for one frame on every
  // dashboard load for a school that had dismissed it (change review
  // 2026-09-15, portion 4).
  const [dismissed, setDismissed] = React.useState(() => readDismissed(tenantId));
  // Re-read when the school changes: this component survives a tenant switch.
  React.useEffect(() => {
    setDismissed(readDismissed(tenantId));
  }, [tenantId]);

  const completedItems = React.useMemo(() => {
    const completed: Record<string, boolean> = { ...readJson(completedKey(tenantId)) };

    // Derive from the school's own data. Each is one-way: once a step has
    // been done it stays done, because today's numbers cannot disprove it.
    if ((campusCount ?? 0) > 0) completed.campus = true;
    // "Set up a class" is done once a class exists -- not once a student is
    // enrolled in it (the enrollment-based KPI kept it unticked for a school
    // that had built its classes but not enrolled anyone yet).
    if (
      (metrics?.setup_classes_count ?? 0) > 0 ||
      (metrics?.active_classes ?? 0) > 0
    ) {
      completed.class = true;
    }
    // The step reads "Add a family & student", so a family alone is not it;
    // a family with a student is, enrolled or not.
    if (
      (metrics?.setup_families_with_students_count ?? 0) > 0 ||
      ((metrics?.total_families ?? 0) > 0 && (metrics?.total_students ?? 0) > 0)
    ) {
      completed.family = true;
    }
    if ((feeTypeCount ?? 0) > 0) completed.fees = true;
    // Records today prove attendance has been taken; the flag persists, so a
    // weekend does not un-complete the step.
    if ((metrics?.attendance_total_today ?? 0) > 0) completed.attendance = true;

    return completed;
  }, [metrics, campusCount, feeTypeCount, tenantId]);

  // Persist derived completions outside useMemo
  React.useEffect(() => {
    writeJson(completedKey(tenantId), completedItems);
  }, [completedItems, tenantId]);

  const completedCount = CHECKLIST_ITEMS.filter(
    (item) => completedItems[item.key]
  ).length;
  const progress = (completedCount / CHECKLIST_ITEMS.length) * 100;

  if (dismissed || completedCount === CHECKLIST_ITEMS.length) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissedKey(tenantId), "true");
    } catch {
      // Dismissal is a convenience; ignore storage failures.
    }
    setDismissed(true);
  };

  return (
    <Card
      sx={{
        mb: 4,
        boxShadow: 1,
        border: "1px solid",
        borderColor: alpha("#667eea", 0.2),
        background: `linear-gradient(135deg, ${alpha("#667eea", 0.03)} 0%, ${alpha("#764ba2", 0.03)} 100%)`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2.5
          }}>
          <Stack direction="row" spacing={1.5} sx={{
            alignItems: "center"
          }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: alpha("#667eea", 0.1),
                color: "#667eea",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RocketIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{
                fontWeight: 700
              }}>
                Get Started with IlmIQ
              </Typography>
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                {completedCount} of {CHECKLIST_ITEMS.length} steps completed
              </Typography>
            </Box>
          </Stack>
          <Tooltip title="Dismiss checklist">
            <IconButton size="small" onClick={handleDismiss}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* Progress */}
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label={`Setup progress: ${Math.round(progress)}% complete`}
          sx={{
            mb: 2.5,
            height: 6,
            borderRadius: 3,
            bgcolor: alpha("#667eea", 0.1),
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
            },
          }}
        />

        {/* Items */}
        <Stack spacing={1}>
          {CHECKLIST_ITEMS.map((item) => {
            const isCompleted = completedItems[item.key];
            return (
              <Box
                key={item.key}
                // A visible shortcut must be reachable without a mouse. These
                // were clickable divs, so keyboard and screen-reader users
                // could see the step but not start it (audit Q05). A completed
                // step is not an action, so it is not a focus stop.
                component={isCompleted ? "div" : "button"}
                type={isCompleted ? undefined : "button"}
                onClick={isCompleted ? undefined : () => navigate(item.path)}
                // Named by its content (label + description). A hand-written
                // aria-label repeated the same text but could never match
                // the visible text exactly (WCAG 2.5.3 label in name).
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  width: "100%",
                  textAlign: "left",
                  font: "inherit",
                  color: "inherit",
                  border: "none",
                  bgcolor: "transparent",
                  borderRadius: 1.5,
                  cursor: isCompleted ? "default" : "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": isCompleted
                    ? {}
                    : {
                        bgcolor: alpha(item.color, 0.06),
                      },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: item.color,
                    outlineOffset: 2,
                  },
                }}
              >
                {isCompleted ? (
                  <CheckIcon sx={{ fontSize: 22, color: "success.main" }} />
                ) : (
                  <UncheckedIcon
                    sx={{ fontSize: 22, color: "grey.400" }}
                  />
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      color: isCompleted ? "text.disabled" : item.color,
                      display: "flex",
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        textDecoration: isCompleted ? "line-through" : "none",
                        color: isCompleted ? "text.disabled" : "text.primary",
                      }}
                    >
                      {item.label}
                    </Typography>
                    {/* Word gap so the name reads "label description", not
                        "labeldescription". */}
                    {!isCompleted && " "}
                    {!isCompleted && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.3
                        }}>
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
